<template>
  <div class="elevation-profile-chart">
    <div class="chart-header d-flex justify-content-between align-items-center mb-2">
      <h6 class="chart-title m-0">Elevation Profile</h6>
      <div class="chart-stats">
        <small class="text-muted">
          Max: {{ maxElevation }} | Min: {{ minElevation }}
        </small>
      </div>
    </div>

    <div class="chart-container" ref="chartContainer">
      <svg
        :width="chartWidth"
        :height="chartHeight"
        class="elevation-svg"
        @mousemove="handleMouseMove"
        @mouseleave="handleMouseLeave"
      >
        <!-- Background grid -->
        <g class="grid-lines">
          <line
            v-for="(line, index) in gridLines"
            :key="`grid-${index}`"
            :x1="line.x1"
            :y1="line.y1"
            :x2="line.x2"
            :y2="line.y2"
            stroke="rgba(255, 255, 255, 0.1)"
            stroke-width="1"
          />
        </g>

        <!-- Elevation line -->
        <path
          :d="elevationPath"
          fill="none"
          stroke="#4CAF50"
          stroke-width="2"
          class="elevation-line"
        />

        <!-- Area fill under the line -->
        <path
          :d="areaPath"
          fill="rgba(76, 175, 80, 0.2)"
          class="elevation-area"
        />

        <!-- Hover indicator -->
        <g v-if="hoverPoint" class="hover-indicator">
          <line
            :x1="hoverPoint.x"
            :y1="0"
            :x2="hoverPoint.x"
            :y2="chartHeight"
            stroke="rgba(255, 255, 255, 0.5)"
            stroke-width="1"
            stroke-dasharray="4,4"
          />
          <circle
            :cx="hoverPoint.x"
            :cy="hoverPoint.y"
            r="4"
            fill="#4CAF50"
            stroke="#fff"
            stroke-width="1.5"
          />
          <text
            :x="hoverPoint.x"
            :y="hoverPoint.y - 10"
            text-anchor="middle"
            fill="#fff"
            font-size="10"
            font-weight="bold"
          >
            {{ hoverPoint.elevation }}
          </text>
        </g>

        <!-- Axis labels -->
        <g class="axis-labels">
          <!-- Y-axis labels (elevation) - positioned outside left edge -->
          <text
            v-for="(label, index) in yAxisLabels"
            :key="`y-label-${index}`"
            :x="0"
            :y="label.y"
            text-anchor="start"
            fill="rgba(255, 255, 255, 0.7)"
            font-size="8"
            font-weight="500"
            dominant-baseline="middle"
          >
            {{ label.value }}
          </text>

          <!-- X-axis labels (distance) - positioned below chart -->
          <text
            v-for="(label, index) in xAxisLabels"
            :key="`x-label-${index}`"
            :x="label.x"
            :y="chartHeight - 20"
            text-anchor="middle"
            fill="rgba(255, 255, 255, 0.7)"
            font-size="8"
            font-weight="500"
            dominant-baseline="hanging"
          >
            {{ label.value }}
          </text>
        </g>
      </svg>
    </div>

  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapGetters, mapActions } from 'vuex';
import * as turf from '@turf/turf';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';

export default defineComponent({
  name: 'MeasurementElevationProfileChart',

  props: {
    width: {
      type: Number,
      default: 280
    },
    height: {
      type: Number,
      default: 120
    }
  },

  data() {
    return {
      hoverPoint: null,
      chartWidth: this.width,
      chartHeight: this.height
    };
  },

  computed: {
    ...mapGetters('hikeMeasurement', [
      'measuredGeoJson',
      'measuredElevationGain'
    ]),

    // The user's measurement preference, used to format elevation/distance
    // labels so the chart matches the rest of the app (e.g. feet vs metres).
    measurementPreference() {
      return MeasurementPrefUtility.preference();
    },

    // Extract elevation data from GeoJSON
    elevationData() {
      if (!this.measuredGeoJson || !this.measuredGeoJson.geometry) {
        console.log('No measured GeoJSON data available');
        return [];
      }

      console.log('GeoJSON geometry type:', this.measuredGeoJson.geometry.type);

      const elevationPoints = [];
      let cumulativeDistance = 0;

      if (this.measuredGeoJson.geometry.type === 'LineString') {
        // Handle LineString geometry
        const coordinates = this.measuredGeoJson.geometry.coordinates;
        if (!Array.isArray(coordinates) || coordinates.length === 0) {
          console.log('Invalid LineString coordinates array:', coordinates);
          return [];
        }

        // Process LineString coordinates
        this.processLineString(coordinates, elevationPoints, cumulativeDistance);

      } else if (this.measuredGeoJson.geometry.type === 'MultiLineString') {
        // Handle MultiLineString geometry
        const lineStrings = this.measuredGeoJson.geometry.coordinates;
        if (!Array.isArray(lineStrings) || lineStrings.length === 0) {
          console.log('Invalid MultiLineString coordinates array:', lineStrings);
          return [];
        }

        console.log('Processing MultiLineString with', lineStrings.length, 'lines');

        // Process each LineString in the MultiLineString
        lineStrings.forEach((lineString, lineIndex) => {
          if (!Array.isArray(lineString) || lineString.length === 0) {
            console.warn('Invalid LineString at index', lineIndex, lineString);
            return;
          }

          console.log(`Processing LineString ${lineIndex} with ${lineString.length} points`);
          cumulativeDistance = this.processLineString(lineString, elevationPoints, cumulativeDistance);
        });

      } else {
        console.warn('Unsupported geometry type:', this.measuredGeoJson.geometry.type);
        return [];
      }

      console.log('Elevation data extracted:', {
        geometryType: this.measuredGeoJson.geometry.type,
        totalPoints: elevationPoints.length,
        maxDistance: elevationPoints.length > 0 ? elevationPoints[elevationPoints.length - 1].distance : 0,
        elevationRange: elevationPoints.length > 0 ? {
          min: Math.min(...elevationPoints.map(p => p.elevation)),
          max: Math.max(...elevationPoints.map(p => p.elevation))
        } : { min: 0, max: 0 }
      });

      return elevationPoints;
    },

    // Chart calculations
    maxElevation() {
      if (this.elevationData.length === 0) return this.measurementPreference.smallUnitFormatter(0);
      const max = Math.max(...this.elevationData.map(p => p.elevation));
      return this.measurementPreference.smallUnitFormatter(max);
    },

    minElevation() {
      if (this.elevationData.length === 0) return this.measurementPreference.smallUnitFormatter(0);
      const min = Math.min(...this.elevationData.map(p => p.elevation));
      return this.measurementPreference.smallUnitFormatter(min);
    },

    // Numeric versions for chart calculations
    maxElevationNumeric() {
      if (this.elevationData.length === 0) return 0;
      return Math.max(...this.elevationData.map(p => p.elevation));
    },

    minElevationNumeric() {
      if (this.elevationData.length === 0) return 0;
      return Math.min(...this.elevationData.map(p => p.elevation));
    },

    totalDistance() {
      if (this.elevationData.length === 0) return '0 m';
      const total = this.elevationData[this.elevationData.length - 1].distance;
      const measurementPreference = MeasurementPrefUtility.preference();
      return measurementPreference.smallUnitFormatter(total, true);
    },

    // SVG path generation
    elevationPath() {
      if (this.elevationData.length < 2) return '';
      if (this.maxDistance <= 0 || this.chartWidth <= 0 || this.chartHeight <= 0) return '';

      const points = this.elevationData.map((point, index) => {
        const x = (point.distance / this.maxDistance) * (this.chartWidth - 30) + 20; // Adjusted for left margin
        const y = this.chartHeight - 25 - ((point.elevation - this.minElevationNumeric) / this.elevationRange) * (this.chartHeight - 45); // Adjusted for bottom margin

        // Ensure we have valid numbers
        const validX = isNaN(x) ? 20 : Math.max(20, Math.min(x, this.chartWidth - 10));
        const validY = isNaN(y) ? this.chartHeight - 25 : Math.max(15, Math.min(y, this.chartHeight - 25));

        return `${index === 0 ? 'M' : 'L'} ${validX} ${validY}`;
      });

      return points.join(' ');
    },

    areaPath() {
      if (this.elevationData.length < 2) return '';
      if (this.maxDistance <= 0 || this.chartWidth <= 0 || this.chartHeight <= 0) return '';

      const points = this.elevationData.map((point, index) => {
        const x = (point.distance / this.maxDistance) * (this.chartWidth - 30) + 20; // Adjusted for left margin
        const y = this.chartHeight - 25 - ((point.elevation - this.minElevationNumeric) / this.elevationRange) * (this.chartHeight - 45); // Adjusted for bottom margin

        // Ensure we have valid numbers
        const validX = isNaN(x) ? 20 : Math.max(20, Math.min(x, this.chartWidth - 10));
        const validY = isNaN(y) ? this.chartHeight - 25 : Math.max(15, Math.min(y, this.chartHeight - 25));

        return `${index === 0 ? 'M' : 'L'} ${validX} ${validY}`;
      });

      const lastPoint = this.elevationData[this.elevationData.length - 1];
      const lastX = (lastPoint.distance / this.maxDistance) * (this.chartWidth - 30) + 20;
      const validLastX = isNaN(lastX) ? this.chartWidth - 10 : Math.max(20, Math.min(lastX, this.chartWidth - 10));

      return `${points.join(' ')} L ${validLastX} ${this.chartHeight - 25} L 20 ${this.chartHeight - 25} Z`;
    },

    // Helper properties for chart calculations
    maxDistance() {
      if (this.elevationData.length === 0) return 1;
      return this.elevationData[this.elevationData.length - 1].distance;
    },

    elevationRange() {
      const range = this.maxElevationNumeric - this.minElevationNumeric;
      return range === 0 ? 1 : range;
    },

    // Grid lines for the chart
    gridLines() {
      const lines = [];
      const numHorizontalLines = 4;
      const numVerticalLines = 4;

      // Horizontal lines (elevation)
      for (let i = 0; i <= numHorizontalLines; i++) {
        const y = 15 + (i * (this.chartHeight - 45) / numHorizontalLines);
        lines.push({
          x1: 20,
          y1: y,
          x2: this.chartWidth - 10,
          y2: y
        });
      }

      // Vertical lines (distance)
      for (let i = 0; i <= numVerticalLines; i++) {
        const x = 20 + (i * (this.chartWidth - 30) / numVerticalLines);
        lines.push({
          x1: x,
          y1: 15,
          x2: x,
          y2: this.chartHeight - 25
        });
      }

      return lines;
    },

    // Y-axis labels (elevation)
    yAxisLabels() {
      if (this.elevationData.length === 0) return [];

      const labels = [];
      const numLabels = 4;

      for (let i = 0; i <= numLabels; i++) {
        const y = 15 + (i * (this.chartHeight - 45) / numLabels);
        const elevationValue = this.minElevationNumeric + (1 - (i / numLabels)) * this.elevationRange;

        labels.push({
          y: y,
          value: this.measurementPreference.smallUnitFormatter(elevationValue)
        });
      }

      return labels;
    },

    // X-axis labels (distance)
    xAxisLabels() {
      if (this.elevationData.length === 0 || this.maxDistance <= 0) return [];

      const labels = [];
      const numLabels = 4;
      const measurementPreference = MeasurementPrefUtility.preference();

      for (let i = 0; i <= numLabels; i++) {
        const x = 20 + (i * (this.chartWidth - 30) / numLabels);
        const distanceValue = (i / numLabels) * this.maxDistance;

        labels.push({
          x: x,
          value: measurementPreference.smallUnitFormatter(distanceValue, false)
        });
      }

      return labels;
    }
  },

  methods: {
    ...mapActions('hikeMeasurement', ['setHoverCoordinate']),

    // Process a LineString and add elevation points to the array
    processLineString(coordinates, elevationPoints, cumulativeDistance) {
      if (!Array.isArray(coordinates) || coordinates.length === 0) {
        return cumulativeDistance;
      }

      // First point
      const firstCoord = coordinates[0];
      if (Array.isArray(firstCoord) && firstCoord.length >= 2) {
        // Always add first point of each LineString
        elevationPoints.push({
          distance: cumulativeDistance,
          elevation: firstCoord[2] || 0,
          coordinate: firstCoord
        });
      }

      // Calculate distances between points
      for (let i = 1; i < coordinates.length; i++) {
        const prevCoord = coordinates[i - 1];
        const currCoord = coordinates[i];

        // Validate coordinates
        if (!Array.isArray(prevCoord) || !Array.isArray(currCoord) ||
            prevCoord.length < 2 || currCoord.length < 2) {
          console.warn('Invalid coordinate format at index', i, { prevCoord, currCoord });
          continue;
        }

        // Calculate 2D distance (ignore elevation for distance calculation)
        const distance = turf.distance(
          turf.point([prevCoord[0], prevCoord[1]]),
          turf.point([currCoord[0], currCoord[1]]),
          { units: 'meters' }
        );

        // Ensure distance is a valid number
        if (isNaN(distance) || !isFinite(distance)) {
          console.warn('Invalid distance calculated at index', i, { distance, prevCoord, currCoord });
          continue;
        }

        cumulativeDistance += distance;

        elevationPoints.push({
          distance: cumulativeDistance,
          elevation: currCoord[2] || 0,
          coordinate: currCoord
        });
      }

      return cumulativeDistance;
    },

    handleMouseMove(event) {
      if (this.elevationData.length < 2) return;

      const rect = this.$refs.chartContainer.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;

      // Find the closest point
      const closestPoint = this.findClosestPoint(mouseX);

      if (closestPoint) {
        this.hoverPoint = {
          x: closestPoint.x,
          y: closestPoint.y,
          elevation: this.measurementPreference.smallUnitFormatter(closestPoint.elevation),
          coordinate: closestPoint.coordinate
        };

        // Update Vuex with the coordinate for map marker
        if (closestPoint.coordinate) {
          this.setHoverCoordinate([closestPoint.coordinate[0], closestPoint.coordinate[1]]);
        }
      }
    },

    handleMouseLeave() {
      this.hoverPoint = null;
      // Clear the hover coordinate in Vuex
      this.setHoverCoordinate(null);
    },

    findClosestPoint(mouseX) {
      let closestPoint = null;
      let minDistance = Infinity;

      if (this.elevationData.length === 0 || this.maxDistance <= 0) return null;

      this.elevationData.forEach(point => {
        const x = (point.distance / this.maxDistance) * (this.chartWidth - 30) + 20; // Adjusted for left margin
        const y = this.chartHeight - 25 - ((point.elevation - this.minElevationNumeric) / this.elevationRange) * (this.chartHeight - 45); // Adjusted for bottom margin

        // Skip invalid coordinates
        if (isNaN(x) || isNaN(y)) return;

        const distance = Math.abs(x - mouseX);

        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = {
            x,
            y,
            elevation: point.elevation,
            coordinate: point.coordinate // Include the coordinate for map marker
          };
        }
      });

      return closestPoint;
    }
  },

  mounted() {
    // Update chart dimensions based on container
    if (this.$refs.chartContainer) {
      const containerWidth = this.$refs.chartContainer.clientWidth;
      if (containerWidth > 0) {
        this.chartWidth = containerWidth;
      }
    }
  }
});
</script>

<style scoped>
.elevation-profile-chart {
  background-color: rgba(45, 45, 45, 0.8);
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.chart-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 8px;
}

.chart-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
}

.chart-stats {
  font-size: 0.75rem;
}

.chart-container {
  width: 100%;
  overflow: hidden;
}

.elevation-svg {
  display: block;
  width: 100%;
  height: 100%;
}

.elevation-line {
  transition: stroke-width 0.2s ease;
}

.elevation-line:hover {
  stroke-width: 3;
}

.hover-indicator {
  pointer-events: none;
}

.axis-labels {
  pointer-events: none;
}

.chart-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 8px;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .elevation-profile-chart {
    padding: 8px;
  }

  .chart-title {
    font-size: 0.85rem;
  }

  .chart-stats {
    font-size: 0.7rem;
  }
}
</style>
